import React from 'react';
import BaseClass from 'components/BaseClass';
import PropTypes from 'prop-types';
import FrameworkUtils from 'utils/framework';
import AccountLayout from 'components/RichProfile/MyAccount/AccountLayout/AccountLayout';
import { Divider } from 'components/ui';
import PromotionalEmailsPrefs from 'components/RichProfile/MyAccount/MailingPrefs/PromotionalEmailsPrefs/PromotionalEmailsPrefs';
import NotificationsAndRemindersPrefs from 'components/RichProfile/MyAccount/MailingPrefs/NotificationsAndRemindersPrefs/NotificationsAndRemindersPrefs';
import PostalMailPrefs from 'components/RichProfile/MyAccount/MailingPrefs/PostalMailPrefs/PostalMailPrefs';
import PleaseSignInBlock from 'components/RichProfile/MyAccount/PleaseSignIn';
import languageLocaleUtils from 'utils/LanguageLocale';

import UserUtils from 'utils/User';
import sessionExtensionService from 'services/SessionExtensionService';

import MailingPrefsEN from 'components/RichProfile/MyAccount/MailingPrefs/locales/MailingPrefs_en_US';
import MailingPrefsFR from 'components/RichProfile/MyAccount/MailingPrefs/locales/MailingPrefs_fr_CA';

import CommunicationLanguagePrefs from 'components/RichProfile/MyAccount/MailingPrefs/CommunicationLanguagePrefs/CommunicationLanguagePrefs';

const { wrapComponent } = FrameworkUtils;

class MailingPrefs extends BaseClass {
    state = {
        shouldShowPostalMailPrefs: UserUtils.getBiAccountInfo(this.props?.user)?.vibSegment !== UserUtils.types.NON_BI,
        sections: {
            CommunicationLanguagePrefs: { editMode: false },
            PromotionalEmailsPrefs: { editMode: false },
            NotificationsAndRemindersPrefs: { editMode: false },
            PostalMailPrefs: { editMode: false }
        },
        subscriptionPreferredLanguage: '',
        subscriptionPreferredCountry: languageLocaleUtils.getCurrentCountry(),
        subscriptionPreferredZipCode: ''
    };

    fetchPreferences = () => {
        const userIsAnonymous = UserUtils.isAnonymous(this.props.auth);
        !userIsAnonymous && this.props.fetchAndStorePreferences();
    };

    componentDidMount() {
        this.fetchPreferences();
        Sephora.isDesktop() && sessionExtensionService.setExpiryTimer(this.props.requestCounter);
    }

    componentDidUpdate(prevProps) {
        const userIsAnonymous = UserUtils.isAnonymous(this.props.auth);

        if (!userIsAnonymous && this.props.auth?.hasIdentity !== null && prevProps.user?.profileId !== this.props.user?.profileId) {
            this.props.fetchAndStorePreferences();
        }
    }

    handleSectionExpand = sectionComponent => {
        const sections = {
            CommunicationLanguagePrefs: { editMode: false },
            PromotionalEmailsPrefs: { editMode: false },
            NotificationsAndRemindersPrefs: { editMode: false },
            PostalMailPrefs: { editMode: false }
        };

        sections[sectionComponent] = { editMode: true };

        this.setState({ sections });
    };

    shouldRenderCommunicationLanguagePrefs() {
        return Sephora?.configurationSettings?.communicationLanguagePrefsEnabled || false;
    }

    // Captures the user's selected language preferences (Language, country and Zip Code).
    // The delegated functions should return the country and language following the format:
    // - Country: 'CA' or 'US'
    // - Language: 'fr_CA', 'en_US' or 'en_CA'
    onLocaleChange = newLanguagePrefs => {
        const { subscriptionPreferredLanguage, subscriptionPreferredCountry, subscriptionPreferredZipCode } = newLanguagePrefs || {};

        // Extracts the language ('US') from the language code ('en_US')
        const normalizedLanguage =
            typeof subscriptionPreferredLanguage === 'string' && subscriptionPreferredLanguage.includes('_')
                ? subscriptionPreferredLanguage.split('_')[0].toUpperCase()
                : subscriptionPreferredLanguage;

        this.setState({
            subscriptionPreferredLanguage: normalizedLanguage || '', // Stores the lang as 'EN', 'US' or 'FR'.
            subscriptionPreferredCountry: subscriptionPreferredCountry || '', // Stores the country as 'CA' or 'US'.
            subscriptionPreferredZipCode: subscriptionPreferredZipCode || ''
        });
    };

    onUpdatedPreferences = () => {
        this.fetchPreferences();
    };

    render() {
        const { auth } = this.props;
        const getText = languageLocaleUtils.getLocalisedDataFromResource(MailingPrefsEN, MailingPrefsFR);
        const { subscriptionPreferredLanguage, subscriptionPreferredCountry, subscriptionPreferredZipCode } = this.state;
        const userIsAnonymous = UserUtils.isAnonymous(auth);

        const subscriptionLanguagePreferences = {
            subscriptionPreferredLanguage,
            subscriptionPreferredCountry,
            subscriptionPreferredZipCode
        };

        const sectionDivider = Sephora.isMobile() ? (
            <Divider
                height={3}
                color='nearWhite'
                marginX='-container'
            />
        ) : (
            <Divider />
        );

        return (
            <AccountLayout
                section='account'
                page='mail prefs'
                title={getText('emailAndMailPreferences')}
            >
                {!Sephora.isNodeRender && (
                    <div>
                        {userIsAnonymous && <PleaseSignInBlock />}

                        {!userIsAnonymous && (
                            <div>
                                {this.shouldRenderCommunicationLanguagePrefs() && (
                                    <CommunicationLanguagePrefs
                                        onExpand={() => this.handleSectionExpand('CommunicationLanguagePrefs')}
                                        viewMode={!this.state.sections['CommunicationLanguagePrefs'].editMode}
                                        onLocaleChange={this.onLocaleChange}
                                        onUpdatedPreferences={this.onUpdatedPreferences}
                                    />
                                )}
                                <PromotionalEmailsPrefs
                                    ref={comp => (this._promotionalEmailPrefs = comp)}
                                    onExpand={() => this.handleSectionExpand('PromotionalEmailsPrefs')}
                                    viewMode={!this.state.sections['PromotionalEmailsPrefs'].editMode}
                                    subscriptionLanguagePreferences={subscriptionLanguagePreferences}
                                    onUpdatedPreferences={this.onUpdatedPreferences}
                                />

                                {sectionDivider}
                                <NotificationsAndRemindersPrefs
                                    ref={comp => (this._notificationsAndRemindersPrefs = comp)}
                                    onExpand={() => this.handleSectionExpand('NotificationsAndRemindersPrefs')}
                                    viewMode={!this.state.sections['NotificationsAndRemindersPrefs'].editMode}
                                    subscriptionLanguagePreferences={subscriptionLanguagePreferences}
                                    onUpdatedPreferences={this.onUpdatedPreferences}
                                />

                                {this.state.shouldShowPostalMailPrefs && (
                                    <div>
                                        {sectionDivider}
                                        <PostalMailPrefs
                                            ref={comp => (this._postalMailPrefs = comp)}
                                            onExpand={() => this.handleSectionExpand('PostalMailPrefs')}
                                            viewMode={!this.state.sections['PostalMailPrefs'].editMode}
                                            subscriptionLanguagePreferences={subscriptionLanguagePreferences}
                                            onUpdatedPreferences={this.onUpdatedPreferences}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </AccountLayout>
        );
    }
}

MailingPrefs.propTypes = {
    user: PropTypes.object,
    fetchAndStorePreferences: PropTypes.func,
    requestCounter: PropTypes.number
};

export default wrapComponent(MailingPrefs, 'MailingPrefs', true);
